import { useReadContracts } from "wagmi";
import { type Address } from "viem";
import { ADDRESSES } from "../config/contracts";
import {
  QUOTER_ABI,
  CL_FEE_TIERS,
  BIN_STEPS,
  sortCurrencies,
  clParameters,
  binParameters,
  NO_HOOKS,
} from "../config/v4Contracts";
import { type TokenInfo } from "../config/tokens";

export interface BestPool {
  type: "V2" | "CL" | "BIN";
  fee: number;
  tickSpacing?: number;
  binStep?: number;
  label?: string;
  poolKey?: {
    currency0: Address;
    currency1: Address;
    hooks: Address;
    poolManager: Address;
    fee: number;
    parameters: `0x${string}`;
  };
}

export interface V4QuoteResult {
  bestQuote: bigint;
  bestPool: BestPool | null;
  isLoading: boolean;
}

export function useV4Quote(
  tokenIn: TokenInfo,
  tokenOut: TokenInfo,
  amountIn: bigint
): V4QuoteResult {
  const enabled = amountIn > 0n;

  // Resolve effective addresses: native JASMY → WJASMY for pool lookup
  const tokenInAddr: Address = tokenIn.isNative ? ADDRESSES.WJASMY : tokenIn.address;
  const tokenOutAddr: Address = tokenOut.isNative ? ADDRESSES.WJASMY : tokenOut.address;

  const [currency0, currency1] = sortCurrencies(tokenInAddr, tokenOutAddr);
  const zeroForOne = tokenInAddr.toLowerCase() === currency0.toLowerCase();

  // Clamp to uint128 max to avoid overflow in Quoter call
  const exactAmount: bigint =
    amountIn > 0xffffffffffffffffffffffffffffffffn
      ? 0xffffffffffffffffffffffffffffffffn
      : amountIn;

  // Build CL quote calls (4 fee tiers)
  const clContracts = CL_FEE_TIERS.map(({ fee, tickSpacing }) => ({
    address: ADDRESSES.QUOTER as Address,
    abi: QUOTER_ABI,
    functionName: "quoteExactInputSingle" as const,
    args: [
      {
        poolKey: {
          currency0,
          currency1,
          hooks: NO_HOOKS,
          poolManager: ADDRESSES.CL_POOL_MANAGER,
          fee,
          parameters: clParameters(tickSpacing),
        },
        zeroForOne,
        exactAmount,
        hookData: "0x" as `0x${string}`,
      },
    ] as const,
  }));

  // Build BIN quote calls (4 bin steps)
  const binContracts = BIN_STEPS.map(({ binStep }) => ({
    address: ADDRESSES.QUOTER as Address,
    abi: QUOTER_ABI,
    functionName: "quoteExactInputSingle" as const,
    args: [
      {
        poolKey: {
          currency0,
          currency1,
          hooks: NO_HOOKS,
          poolManager: ADDRESSES.BIN_POOL_MANAGER,
          fee: 0,
          parameters: binParameters(binStep),
        },
        zeroForOne,
        exactAmount,
        hookData: "0x" as `0x${string}`,
      },
    ] as const,
  }));

  const allContracts = [...clContracts, ...binContracts];

  const { data, isLoading } = useReadContracts({
    contracts: allContracts,
    query: {
      enabled,
      refetchInterval: 10_000,
      // Quoter calls are state-changing but we use them read-only via multicall
      // Failures on empty pools are expected — handle per-result
    },
  });

  if (!data || !enabled) {
    return { bestQuote: 0n, bestPool: null, isLoading };
  }

  let bestQuote = 0n;
  let bestPool: BestPool | null = null;

  // Evaluate CL results
  CL_FEE_TIERS.forEach(({ fee, tickSpacing, label }, i) => {
    const result = data[i];
    if (result?.status !== "success" || !result.result) return;
    const [amountOut] = result.result as [bigint, bigint];
    if (amountOut > bestQuote) {
      bestQuote = amountOut;
      bestPool = {
        type: "CL",
        fee,
        tickSpacing,
        label,
        poolKey: {
          currency0,
          currency1,
          hooks: NO_HOOKS,
          poolManager: ADDRESSES.CL_POOL_MANAGER,
          fee,
          parameters: clParameters(tickSpacing),
        },
      };
    }
  });

  // Evaluate BIN results
  BIN_STEPS.forEach(({ binStep, label }, i) => {
    const result = data[CL_FEE_TIERS.length + i];
    if (result?.status !== "success" || !result.result) return;
    const [amountOut] = result.result as [bigint, bigint];
    if (amountOut > bestQuote) {
      bestQuote = amountOut;
      bestPool = {
        type: "BIN",
        fee: 0,
        binStep,
        label,
        poolKey: {
          currency0,
          currency1,
          hooks: NO_HOOKS,
          poolManager: ADDRESSES.BIN_POOL_MANAGER,
          fee: 0,
          parameters: binParameters(binStep),
        },
      };
    }
  });

  return { bestQuote, bestPool, isLoading };
}
